import axios from 'axios'
import _ from 'lodash'

import {
  FIELD_TYPES,
  ACTIVITY_TYPES,
  COMPUTED_OPERATION_TYPES,
  PROCESS_STATUSES,
  DROPDOWN_DEFAULT_VALUE,
  COMPUTED_RESERVED_KEYS,
  REVIEW_CONTEXTS,
  PROCESS_ACTIVITY_STATUSES,
  DYNAMIC_FIELD_TYPE,
} from '../constants'
import { getCurrentFinancialYear } from '../utilityFunctions'
import { getFieldId } from '../../pages/DataEntryPage/utils/utils'
import { calculateEffectiveMinutes, minutesToDays } from '../processDashboard/workHourCalculation'

/**
 * Applies `dynamic_field_type.filter` to a raw usersList from the backend.
 * Filter shape (all optional):
 *   { category: [id,...] | null, user_type: [id,...] | null,
 *     work_status: [id,...] | null, is_account_active: boolean (default true) }
 *
 * Each user's metadata now includes category, user_type, work_status,
 * is_account_active (added to getUsersAndTeamsDynamicList backend query).
 */
export function applyUserFilter(usersList, dynamicFieldType) {
  const filter = dynamicFieldType?.filter
  if (!filter) return usersList

  return usersList.filter((user) => {
    const meta = user.metadata || {}

    // is_account_active defaults to true — skip inactive users unless explicitly set to false
    const accountActiveFilter = filter.is_account_active !== false
    if (accountActiveFilter && meta.is_account_active === false) return false

    if (filter.category?.length > 0) {
      if (!filter.category.some((id) => String(id) === String(meta.category))) return false
    }
    if (filter.user_type?.length > 0) {
      if (!filter.user_type.some((id) => String(id) === String(meta.user_type))) return false
    }
    if (filter.work_status?.length > 0) {
      if (!filter.work_status.some((id) => String(id) === String(meta.work_status))) return false
    }

    return true
  })
}

export async function getFormData(companyId, activityTemplateId, processId, userId, axiosOptions) {
  if (companyId == 0 || activityTemplateId == 0) return null
  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

  let activityDatum = {}
  let toRoutes = []
  let prevProcessDataValues = {}
  let userTableConfigs = []
  let queryData = {}
  let tableSequenceAndHeaders = []

  let allResultsPrelude = await Promise.allSettled([
    axios.get(`${NODE_BASE_URL}/template/activity/${activityTemplateId}`, axiosOptions),
    axios.get(`${NODE_BASE_URL}/template/route/activity/to/${activityTemplateId}`, axiosOptions),
    axios.get(
      `${NODE_BASE_URL}/data-entry/process-data/get-previous-process-data-values/${processId}`,
      axiosOptions,
    ),
    axios.get(`${NODE_BASE_URL}/user/user-detail/get-user-table-configs/${userId} `, axiosOptions),
    axios.get(`${NODE_BASE_URL}/data-entry/query/process/${processId} `, axiosOptions),

    axios.get(
      `${NODE_BASE_URL}/template/field/table_sequence_and_headers/${processId}`,
      axiosOptions,
    ),
  ])

  allResultsPrelude.forEach((allRes, i) => {
    if (allRes.status === 'fulfilled') {
      switch (i) {
        case 0:
          activityDatum = allRes.value.data
          break
        case 1:
          toRoutes = allRes.value.data
          break
        case 2:
          prevProcessDataValues = allRes.value.data
          break
        case 3:
          userTableConfigs = allRes.value.data
          break
        case 4:
          queryData = allRes.value.data
          break

        case 5:
          tableSequenceAndHeaders = allRes.value.data
          break
      }
    }
  })

  if (activityDatum?.activity_type === ACTIVITY_TYPES.REVIEW) {
    activityTemplateId = activityDatum.associated_data_entry_activity_template_id
  }

  let allResults = await Promise.allSettled([
    axios.get(`${NODE_BASE_URL}/template/page/activity/${activityTemplateId}`, axiosOptions),
    axios.get(`${NODE_BASE_URL}/template/field/activity/${activityTemplateId}`, axiosOptions),
    axios.get(
      `${NODE_BASE_URL}/template/field/activity/list-data-metadata/${activityTemplateId}`,
      axiosOptions,
    ),
    axios.get(
      `${NODE_BASE_URL}/template/dependency-map/activity/${activityTemplateId}`,
      axiosOptions,
    ),
    axios.get(`${NODE_BASE_URL}/template/list/${companyId}`, axiosOptions),
    axios.get(
      `${NODE_BASE_URL}/user/user-detail/hidden-fields-for-user/${activityTemplateId}/${userId}`,
      axiosOptions,
    ),
    axios.get(
      `${NODE_BASE_URL}/template/field/dependency-map/activity/${activityTemplateId}`,
      axiosOptions,
    ),
    axios.get(`${NODE_BASE_URL}/user/user-detail/users-teams-dynamic-lists`, axiosOptions),

    axios.get(
      `${NODE_BASE_URL}/user/user-detail/user-team-map/company?company_id=${companyId}`,
      axiosOptions,
    ),
    axios.get(
      `${NODE_BASE_URL}/template/field/pivot-filter-fields/${activityTemplateId}`,
      axiosOptions,
    ),
  ])

  let pageData = []
  let fields = []
  let listDataMetadata = []
  let listDataDependencyMap = []
  let lists = []
  let hiddenFields = []
  let fieldTemplateDependencyMap = []
  let usersList = []
  let teamsList = []
  let userTeamMap = []
  let userActivityFilterFieldsData = []

  allResults.forEach((allResult, i) => {
    if (allResult.status === 'fulfilled') {
      switch (i) {
        case 0:
          pageData = allResult.value.data
          break
        case 1:
          fields = allResult.value.data
          break
        case 2:
          listDataMetadata = allResult.value.data
          break
        case 3:
          listDataDependencyMap = allResult.value.data
          break
        case 4:
          lists = allResult.value.data
          break
        case 5:
          hiddenFields = allResult.value.data
          break
        case 6:
          fieldTemplateDependencyMap = allResult.value.data
          break
        case 7:
          teamsList = allResult.value.data.teamsList
          usersList = allResult.value.data.usersList
          break
        case 8:
          userTeamMap = allResult.value.data
          break
        case 9:
          userActivityFilterFieldsData = allResult.value.data
          break
      }
    }
  })

  let hiddenFieldsIds = hiddenFields.map((fields) => fields.id)
  let userActivityFilterFields = userActivityFilterFieldsData.map(
    (it) => it.pivot_field_template_id,
  )

  fields = fields.filter((field) => {
    if (!hiddenFieldsIds.includes(field.id)) {
      return field
    }
  })

  const subFields = fields.filter((field) => field.is_subfield)

  lists = lists.filter((list) => {
    const fieldListIds = fields.map((field) => field.list_id)
    return fieldListIds.includes(list.id)
  })

  const listIds = lists.map((list) => list.id).join(',')

  let listData = []

  if (listIds.length > 0) {
    listData = (
      await axios.get(`${NODE_BASE_URL}/template/list-data/lists/${listIds}`, axiosOptions)
    ).data
  }

  //builds the form data structure that UI can use
  let formData = []

  for (let j = 0; j < fields.length; j++) {
    let tableSubFields = []
    if (fields[j].data_structure === FIELD_TYPES.TABLE) {
      // const subFieldPattern = 'f_' + fields[j].subfield_template_ids.split(',')[0] + '__'

      // const initialTableRowNumbers = await getRowNumbersOfTableInDB(
      //   processId,
      //   subFieldPattern,
      //   axiosOptions,
      //   fields[j].subfield_template_ids
      // )
      const initialTableRowNumbers =
        tableSequenceAndHeaders
          .find((item) => item.table_field_template_id === fields[j].id)
          ?.sequence_header_map.map((it) => it.row) || []

      let initialTableRowCount = initialTableRowNumbers.length
      const tableSubFieldIds = fields[j].subfield_template_ids.split(',').map((id) => parseInt(id))

      const subFieldToAdd = subFields.filter((subField) => tableSubFieldIds.includes(subField.id))

      //cloneDeep here so that we don't point to the same reference.
      for (let y = 0; y < initialTableRowCount; y++) {
        tableSubFields.push(_.cloneDeep(subFieldToAdd))
      }

      initialTableRowNumbers.forEach((rowNumber, y) => {
        for (let x = 0; x < subFieldToAdd.length; x++) {
          tableSubFields[y][x].id += '__' + rowNumber
        }
      })

      for (let k = 0; k < initialTableRowCount; k++) {
        for (let x = 0; x < tableSubFields[0].length; x++) {
          tableSubFields[k][x].listData = []

          if (
            (tableSubFields[k][x].data_structure === FIELD_TYPES.LIST ||
              tableSubFields[k][x].data_structure === FIELD_TYPES.LIST_MULTI) &&
            tableSubFields[k][x].list_id != null &&
            tableSubFields[k][x].dynamic_field_type == null
          ) {
            const subFieldListData = listData
              .filter((listDatum) => {
                if (tableSubFields[k][x].is_pivot_field) {
                  if (
                    listDatum.list_id === tableSubFields[k][x].list_id &&
                    tableSubFields[k][x].import_from_process_template_id
                      ?.split(',')
                      ?.includes(listDatum?.metadata?.process_template_id + '')
                  ) {
                    return listDatum
                  }
                } else {
                  return listDatum.list_id === tableSubFields[k][x].list_id
                }
              })
              .sort((a, b) => a.sequence - b.sequence)

            tableSubFields[k][x].listData.push(...subFieldListData)
          } else if (
            (tableSubFields[k][x].data_structure === FIELD_TYPES.LIST ||
              tableSubFields[k][x].data_structure === FIELD_TYPES.LIST_MULTI) &&
            tableSubFields[k][x].list_id == null &&
            tableSubFields[k][x].dynamic_field_type != null
          ) {
            if (tableSubFields[k][x].dynamic_field_type.type === DYNAMIC_FIELD_TYPE.TEAMS) {
              tableSubFields[k][x].listData.push(...teamsList)
            } else if (tableSubFields[k][x].dynamic_field_type.type === DYNAMIC_FIELD_TYPE.USERS) {
              tableSubFields[k][x].listData.push(
                ...applyUserFilter(usersList, tableSubFields[k][x].dynamic_field_type)
              )
            }
          }
        }
      }
    }

    if (
      (fields[j].data_structure === FIELD_TYPES.LIST ||
        fields[j].data_structure === FIELD_TYPES.LIST_MULTI) &&
      fields[j].list_id != null &&
      fields[j].dynamic_field_type == null
    ) {
      let userActivityFilterData = []
      if (userActivityFilterFields.includes(fields[j].id) && fields[j].is_pivot_field) {
        let filterFieldTemplateId = userActivityFilterFieldsData.filter(
          (it) => it.pivot_field_template_id == fields[j].id,
        )[0].filter_field_template_id

        userActivityFilterData = (
          await axios.get(
            `${NODE_BASE_URL}/data-entry/process-data/pivot-field-filter-data/${fields[j].id}/${userId}`,
            axiosOptions,
          )
        ).data
      }
      fields[j].listData = listData.filter((listDatum) => {
        if (fields[j].is_pivot_field) {
          if (userActivityFilterFields.includes(fields[j].id)) {
            for (let fd of userActivityFilterData) {
              if (
                listDatum.list_id === fields[j].list_id &&
                fd.process_template_id == listDatum?.metadata?.process_template_id &&
                fd.process_reference_num == listDatum?.metadata?.process_reference_num
              ) {
                return listDatum
                break
              }
            }
          } else {
            if (
              listDatum.list_id === fields[j].list_id &&
              fields[j].import_from_process_template_id
                ?.split(',')
                ?.includes(listDatum?.metadata?.process_template_id + '')
            ) {
              return listDatum
            }
          }
        } else {
          return listDatum.list_id === fields[j].list_id
        }
      })
    } else if (
      (fields[j].data_structure === FIELD_TYPES.LIST ||
        fields[j].data_structure === FIELD_TYPES.LIST_MULTI) &&
      fields[j].list_id == null &&
      fields[j].dynamic_field_type != null
    ) {
      if (fields[j].dynamic_field_type.type === DYNAMIC_FIELD_TYPE.TEAMS) {
        let newTeamList = teamsList.filter((team) => {
          if (fields[j].dynamic_field_type?.subtypes?.includes(team.metadata)) {
            return team
          }
        })
        fields[j].listData = newTeamList
      } else if (fields[j].dynamic_field_type.type === DYNAMIC_FIELD_TYPE.USERS) {
        fields[j].listData = applyUserFilter(usersList, fields[j].dynamic_field_type)
      }
    }
    fields[j].subFields = tableSubFields
  }

  //this filter needs to be done near the end because even the subfields are
  //required in the previous loop(s) to create subfields with
  //included list data in a hierarchial manner
  formData = fields.filter((field) => !field.is_subfield)

  //add children field id's into parent field object
  //...this is for general fields
  for (let j = 0; j < formData.length; j++) {
    const currentField = formData[j]
    currentField.childFieldIds = []

    if (
      (currentField.data_structure === FIELD_TYPES.LIST ||
        currentField.data_structure === FIELD_TYPES.LIST_MULTI) &&
      currentField.list_id != null &&
      currentField.dynamic_field_type == null
    ) {
      for (let k = 0; k < formData.length; k++) {
        for (let z = 0; z < fieldTemplateDependencyMap.length; z++) {
          let breakLoop = false
          if (
            fieldTemplateDependencyMap[z].parent_field_template_id == currentField.id &&
            fieldTemplateDependencyMap[z].child_field_template_id == formData[k].id
          ) {
            listDataDependencyMap.every(function (lddm, _index) {
              if (
                currentField.listData.map((listDatum) => listDatum.id).includes(lddm.parent_id) &&
                formData[k].listData.map((listDatum) => listDatum.id).includes(lddm.child_id)
              ) {
                currentField.childFieldIds.push(formData[k].id)
                breakLoop = true
                return false //break the 'every' loop once match for child is found
              } else return true
            })
            if (breakLoop) {
              break
            }
          }
        }
      }
    } else if (
      (currentField.data_structure === FIELD_TYPES.LIST ||
        currentField.data_structure === FIELD_TYPES.LIST_MULTI) &&
      currentField.list_id == null &&
      currentField.dynamic_field_type != null
    ) {
      if (currentField.dynamic_field_type.type === DYNAMIC_FIELD_TYPE.TEAMS) {
        for (let k = 0; k < formData.length; k++) {
          for (let z = 0; z < fieldTemplateDependencyMap.length; z++) {
            let breakLoop = false
            if (
              fieldTemplateDependencyMap[z].parent_field_template_id == currentField.id &&
              fieldTemplateDependencyMap[z].child_field_template_id == formData[k].id
            ) {
              currentField.childFieldIds.push(formData[k].id)
              breakLoop = true
              if (breakLoop) {
                break
              }
            }
          }
        }
      }
    }
  }

  //add children field id's into parent field object
  //...this is for tables' sub fields
  for (let j = 0; j < formData.length; j++) {
    if (formData[j].data_structure === FIELD_TYPES.TABLE) {
      for (let x = 0; x < formData[j].subFields.length; x++) {
        const subFieldList = formData[j].subFields[x]

        for (let k = 0; k < subFieldList.length; k++) {
          const currentSubField = subFieldList[k]
          currentSubField.childFieldIds = []

          if (
            currentSubField.data_structure === FIELD_TYPES.LIST &&
            currentSubField.list_id != null &&
            currentSubField.dynamic_field_type == null
          ) {
            for (let l = 0; l < subFieldList.length; l++) {
              for (let z = 0; z < fieldTemplateDependencyMap.length; z++) {
                let breakLoop = false
                if (
                  fieldTemplateDependencyMap[z].parent_field_template_id ==
                    currentSubField.id.split('__')[0] &&
                  fieldTemplateDependencyMap[z].child_field_template_id ==
                    subFieldList[l].id.split('__')[0]
                ) {
                  listDataDependencyMap.every(function (lddm, _index) {
                    if (
                      currentSubField.listData
                        .map((listDatum) => listDatum.id)
                        .includes(lddm.parent_id) &&
                      subFieldList[l].listData
                        .map((listDatum) => listDatum.id)
                        .includes(lddm.child_id)
                    ) {
                      currentSubField.childFieldIds.push(subFieldList[l].id)
                      breakLoop = true
                      return false //break the 'every' loop once match for child is found
                    } else {
                      return true
                    }
                  })
                  if (breakLoop) {
                    break
                  }
                }
              }
            }
          } else if (
            currentSubField.data_structure === FIELD_TYPES.LIST &&
            currentSubField.list_id == null &&
            currentSubField.dynamic_field_type != null
          ) {
            if (currentSubField.dynamic_field_type.type === DYNAMIC_FIELD_TYPE.TEAMS) {
              for (let k = 0; k < subFieldList.length; k++) {
                for (let z = 0; z < fieldTemplateDependencyMap.length; z++) {
                  let breakLoop = false
                  if (
                    fieldTemplateDependencyMap[z].parent_field_template_id ==
                      currentSubField.id.split('__')[0] &&
                    fieldTemplateDependencyMap[z].child_field_template_id ==
                      subFieldList[k].id.split('__')[0]
                  ) {
                    currentSubField.childFieldIds.push(subFieldList[k].id)
                    breakLoop = true

                    if (breakLoop) {
                      break
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  //construct obj with key (fieldId) and value (field template name)
  let fieldIdFieldValueObj = {}
  formData?.forEach((field) => {
    fieldIdFieldValueObj = {
      ...fieldIdFieldValueObj,
      ['' + field.id]: field.field_template_name,
    }
    if (field.data_structure === FIELD_TYPES.TABLE) {
      field?.subFields[0]?.forEach((subField) => {
        fieldIdFieldValueObj = {
          ...fieldIdFieldValueObj,
          ['' + subField.id.split('__')[0]]: subField.field_template_name,
        }
      })
    }
  })

  //replace placeholders in general fields and table sub fields
  formData?.forEach((field, z) => {
    if (field.data_structure != FIELD_TYPES.TABLE) {
      field.listData?.forEach((listDatum, i) => {
        let text = listDatum.value
        let pattern = /\[\[\d+\]\]/g
        let placeHoldersIds = text.match(pattern)

        placeHoldersIds?.forEach((ph) => {
          ph = ph.replace('[[', '').replace(']]', '')
          const replacementPlaceHolderText = fieldIdFieldValueObj[ph]
          text = text.replace(`[[${ph}]]`, `[[${replacementPlaceHolderText}]]`)

          formData[z].listData[i].value = text
        })
      })
    } else if (field.data_structure == FIELD_TYPES.TABLE) {
      field?.subFields?.forEach((subFieldRow, i) => {
        subFieldRow?.forEach((subField, j) => {
          if (subField.data_structure == FIELD_TYPES.LIST) {
            subField.listData?.forEach((listDatum, k) => {
              let text = listDatum.value
              let pattern = /\[\[\d+\]\]/g
              let placeHoldersIds = text.match(pattern)
              placeHoldersIds?.forEach((ph) => {
                ph = ph.replace('[[', '').replace(']]', '')
                const replacementPlaceHolderText = fieldIdFieldValueObj[ph]
                text = text.replace(`[[${ph}]]`, `[[${replacementPlaceHolderText}]]`)

                formData[z].subFields[i][j].listData[k].value = text
              })
            })
          }
        })
      })
    }
  })

  const formDataUnaltered = _.cloneDeep(formData)

  return {
    formData,
    listDataDependencyMap,
    formDataUnaltered,
    pageData,
    listDataMetadata,
    toRoutes,
    activityDatum,
    prevProcessDataValues,
    userTableConfigs,
    queryData,
    userTeamMap,
  }
}

export const getTableRowFromLegacyAllValues = (allValues, columnIds) => {
  const columnRows = []

  let isKeyPresent = false

  for (const columnId of columnIds) {
    if (isKeyPresent) {
      break
    }

    Object.keys(allValues).forEach((it) => {
      const fieldKey = it.split('__')[0] // f_8160__1 -> f_8160
      if (fieldKey === getFieldId(columnId)) {
        isKeyPresent = true
        columnRows.push(Number(it.split('__')[1]))
      }
    })
  }

  return columnRows
}

export async function getRowNumbersOfTableInDB(
  processId,
  subFieldPattern,
  axiosOptions,
  columnIds,
) {
  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL
  const processData = (
    await axios.get(`${NODE_BASE_URL}/data-entry/process-data/${processId}`, axiosOptions)
  ).data
  const processDatum = processData[0].data
  const RowNumbers = []

  Object.keys(processDatum).forEach((key) => {
    if (key.includes(subFieldPattern)) {
      let rowNumber = key.split('__')[1]
      RowNumbers.push(Number(rowNumber))
    }
  })

  if (RowNumbers.length <= 0) {
    return getTableRowFromLegacyAllValues(processDatum, columnIds.split(','))
  }
  return RowNumbers
}

export function isSubFieldCheck(allValues, fieldId) {
  const testPattern = 'f_' + fieldId + '__'
  const keys = Object.keys(allValues)

  for (const key of keys) {
    if (key.startsWith(testPattern)) {
      return true
    }
  }

  return false
}

export async function getComputedRegularFieldsObj(
  formData,
  allValues,
  listDataMetadata,
  selectedRows,
  reviewContext,
  processPrefix,
  processReferenceNum,
  billingProcessData,
  axiosOptions,
  processTemplateId,
  version,
) {
  //computed fields for regular fields
  const computedRegularFields = formData?.filter((field) => field.is_computed === true)

  let computedRegularFieldsObj = {}
  const currentProcessReferenceNo = processReferenceNum
  const currentProcessTemplateId = processTemplateId
  const regularField = await computedRegularFields.map(async (computedField) => {
    const operation = computedField.metadata.operation
    const params = computedField.metadata.params
    const counterFieldListId = allValues['f_' + computedField?.metadata?.counterFieldTemplateId]
    const pivotFieldListId = allValues['f_' + computedField?.metadata?.pivotFieldTemplateId]
    const computedFieldId = computedField.id

    const pivotFieldTemplateId = computedField?.metadata?.pivotFieldTemplateId
    const exclusions = computedField?.metadata?.exclusions

    function isNumeric(value) {
      return /^\d+$/.test(value)
    }

    async function counterApiCall() {
      const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

      if (isNumeric(pivotFieldListId)) {
        try {
          const result = await axios.post(
            `${NODE_BASE_URL}/data-entry/process-data/compute-counter`,
            {
              counterFieldListId,
              pivotFieldListId,
              computedFieldId,
              currentProcessTemplateId,
              currentProcessReferenceNo,
            },
            axiosOptions,
          )
          return result?.data?.counter
        } catch (error) {
          console.error('Error:', error)
        }
      }
    }
    let computedValue = ''

    if (
      [
        COMPUTED_OPERATION_TYPES.CONCAT,
        COMPUTED_OPERATION_TYPES.MULTIPLY,
        COMPUTED_OPERATION_TYPES.SUM,
      ].includes(operation)
    ) {
      computedValue = await computeForRegularFields(
        params,
        formData,
        allValues,
        listDataMetadata,
        operation,
        processPrefix,
        processReferenceNum,
        billingProcessData,
        counterFieldListId,
        pivotFieldListId,
        counterApiCall,
        computedFieldId,
        version,
      )
    } else if (
      [
        COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN,
        COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN,
        COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN,
      ].includes(operation)
    ) {
      computedValue = computeTableColumnForRegularFields(
        params,
        formData,
        allValues,
        listDataMetadata,
        operation,
        selectedRows,
        reviewContext,
      )
    } else if (COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS === operation) {
      computedValue = await computeFieldAcrossProcess(
        params,
        pivotFieldTemplateId,
        pivotFieldListId,
        exclusions,
        operation,
        processTemplateId,
        axiosOptions,
      )
    }
    computedRegularFieldsObj = {
      ...computedRegularFieldsObj,
      ['f_' + computedField.id]: computedValue,
    }

    return computedRegularFieldsObj
  })

  await regularField.reduce(async (accumulator, item) => {
    accumulator = await accumulator
    await Promise.resolve(item)
    return accumulator
  }, Promise.resolve())

  return computedRegularFieldsObj
}

export async function getComputedSubFieldsObj(formData, allValues, listDataMetadata) {
  //computed fields for table's sub fields
  let computedSubFieldsObj = {}

  let tableFields = formData?.filter((field) => field.data_structure === FIELD_TYPES.TABLE)

  for (let i = 0; i < tableFields.length; i++) {
    const tableField = tableFields[i]
    for (let j = 0; j < tableField.subFields.length; j++) {
      const subFieldRow = tableField.subFields[j]
      const rowNum = subFieldRow[0].id.split('__')[1]

      for (let k = 0; k < subFieldRow.length; k++) {
        const subFieldCol = subFieldRow[k]
        if (subFieldCol.is_computed === true) {
          const operation = subFieldCol.metadata.operation
          const params = subFieldCol.metadata.params
          const subFieldId = subFieldCol.id
          const computedValue = await computeForSubFields(
            params,
            formData,
            allValues,
            listDataMetadata,
            operation,
            rowNum,
            subFieldId,
          )
          computedSubFieldsObj = {
            ...computedSubFieldsObj,
            ['f_' + subFieldCol.id]: computedValue,
          }
        }
      }
    }
  }

  return computedSubFieldsObj
}

async function computeForRegularFields(
  params,
  formData,
  allValues,
  listDataMetadata,
  operation,
  processPrefix,
  processReferenceNum,
  billingProcessData,
  counterFieldListId,
  pivotFieldListId,
  counterApiCall,
  computedFieldId,
  version,
) {
  let computedValue
  let initValue = '' //assume COMPUTED_OPERATION_TYPES.CONCAT

  if (operation === COMPUTED_OPERATION_TYPES.SUM) {
    initValue = 0
  } else if (operation === COMPUTED_OPERATION_TYPES.MULTIPLY) {
    initValue = 1
  }

  computedValue = initValue

  const ComputedData = Promise.all(
    params.map(async (param) => {
      //  Param start without "^^" --> Take literal value of param which store in db
      //  Param start with "^^" --> Take value from Fields
      //  Param start with "**" --> Take value of Keys
      if (!param.startsWith('^^') && !param.startsWith('**')) {
        computedValue += param ? param : initValue //responsibility for DB to store correct types
      } else if (param.startsWith('^^')) {
        let fieldId
        let plusOrMinusOne = 1
        if (
          operation === COMPUTED_OPERATION_TYPES.MULTIPLY ||
          operation === COMPUTED_OPERATION_TYPES.SUM
        ) {
          fieldId = param.substring(3).split('.')[0]
          plusOrMinusOne = param.substring(2).split('.')[0].charAt(0) === '-' ? -1 : 1
        } else {
          fieldId = param.substring(2).split('.')[0]
        }

        const key = param.substring(2).split('.')[1]

        if (key === COMPUTED_RESERVED_KEYS.VALUE) {
          let fieldData = allValues['f_' + fieldId]
          for (let i = 0; i < formData?.length; i++) {
            if (fieldId == formData[i].id) {
              if (formData[i].data_structure === FIELD_TYPES.LIST) {
                if (fieldData !== DROPDOWN_DEFAULT_VALUE) {
                  formData[i]?.listData?.forEach((listDatum) => {
                    if (fieldData == listDatum.id) {
                      fieldData = listDatum.value
                    }
                  })
                } else {
                  fieldData = ''
                }
              } else if (formData[i].data_structure === FIELD_TYPES.LIST_MULTI) {
                // LIST_MULTI stores an array of selected IDs — map each to its label
                const selectedIds = Array.isArray(fieldData) ? fieldData : []
                const labels = selectedIds
                  .map((selectedId) => {
                    const match = formData[i]?.listData?.find((ld) => ld.id == selectedId)
                    return match?.value ?? ''
                  })
                  .filter(Boolean)
                fieldData = labels.join(', ')
              }
            }
          }
          if (
            operation === COMPUTED_OPERATION_TYPES.MULTIPLY ||
            operation === COMPUTED_OPERATION_TYPES.SUM
          ) {
            switch (operation) {
              case COMPUTED_OPERATION_TYPES.MULTIPLY:
                computedValue *= fieldData * plusOrMinusOne
                break
              case COMPUTED_OPERATION_TYPES.SUM:
              default:
                computedValue += fieldData * plusOrMinusOne
            }
          } else {
            //assume || COMPUTED_OPERATION_TYPES.CONCAT
            computedValue += fieldData
          }
        } else {
          const rawValue = allValues['f_' + fieldId]

          for (let i = 0; i < formData?.length; i++) {
            if (fieldId == formData[i]?.id) {
              const isMultiList = formData[i]?.data_structure === FIELD_TYPES.LIST_MULTI
              const isDynamic = formData[i]?.list_id == null && formData[i]?.dynamic_field_type

              if (isDynamic && operation === COMPUTED_OPERATION_TYPES.CONCAT) {
                // dynamic user/team field — look up metadata from formData[i].listData
                // (listDataMetadata has no entries for dynamic fields)
                const selectedIds = Array.isArray(rawValue)
                  ? rawValue
                  : rawValue
                    ? String(rawValue).split(',').map((s) => s.trim()).filter(Boolean)
                    : []
                const metaValues = selectedIds
                  .map((selectedId) => {
                    const item = formData[i]?.listData?.find((ld) => ld.id == selectedId)
                    return item?.metadata?.[key] ?? ''
                  })
                  .filter(Boolean)
                computedValue += metaValues.join(', ')
              } else if (isMultiList && operation === COMPUTED_OPERATION_TYPES.CONCAT) {
                // static list-multi — IDs stored as an array, look up metadata for each
                const selectedIds = Array.isArray(rawValue)
                  ? rawValue
                  : rawValue
                    ? String(rawValue).split(',').map((s) => s.trim()).filter(Boolean)
                    : []
                const metaValues = selectedIds
                  .map((ldId) => {
                    const ldmdMatch = listDataMetadata.find(
                      (lddm) => lddm.id == fieldId && lddm.list_data_id == ldId,
                    )
                    return ldmdMatch?.list_data_metadata == null
                      ? ''
                      : ldmdMatch?.list_data_metadata[key] ?? ''
                  })
                  .filter(Boolean)
                computedValue += metaValues.join(', ')
              } else {
                // single-select list (or SUM/MULTIPLY — use first/only value)
                const ldmdMatch = listDataMetadata.find((lddm) => {
                  return lddm.id == fieldId && lddm.list_data_id == rawValue
                })

                const match =
                  ldmdMatch?.list_data_metadata == null
                    ? initValue
                    : ldmdMatch?.list_data_metadata[key]
                if (operation === COMPUTED_OPERATION_TYPES.MULTIPLY) {
                  computedValue *= match * plusOrMinusOne
                } else if (operation === COMPUTED_OPERATION_TYPES.SUM) {
                  computedValue += match * plusOrMinusOne
                } else {
                  //assume COMPUTED_OPERATION_TYPES.CONCAT
                  computedValue += match
                }
              }
            }
          }
        }
      } else if (param.startsWith('**') && operation === COMPUTED_OPERATION_TYPES.CONCAT) {
        const key = param.substring(2)
        if (key === COMPUTED_RESERVED_KEYS.PROCESS_PREFIX) {
          computedValue += processPrefix
        } else if (key === COMPUTED_RESERVED_KEYS.PROCESS_REFERENCE_NUM) {
          computedValue += processReferenceNum
        } else if (key === COMPUTED_RESERVED_KEYS.CURRENT_FINANCIAL_YEAR) {
          let currentFinancialYear = getCurrentFinancialYear()
          computedValue += currentFinancialYear
        } else if (key === COMPUTED_RESERVED_KEYS.COUNTER) {
          function isNumeric(value) {
            return /^\d+$/.test(value)
          }

          const filedIdInAllValuesLength = Object.keys(allValues).filter((key) =>
            isNumeric(allValues[key]),
          ).length

          const maxVersionMap = new Map()
          billingProcessData.forEach((item) => {
            const { process_reference_num, version } = item
            if (
              !maxVersionMap.has(process_reference_num) ||
              version > maxVersionMap.get(process_reference_num)
            ) {
              maxVersionMap.set(process_reference_num, version)
            }
          })

          // Filter the data to include only the items with the maximum version for each process_reference_num
          const filteredData = billingProcessData.filter(
            (item) => item.version === maxVersionMap.get(item.process_reference_num),
          )

          if (isNumeric(counterFieldListId) && isNumeric(pivotFieldListId)) {
            const filterData = filteredData.filter((item) => {
              let flag = 0
              const itemData = item.data
              Object.keys(itemData).forEach((key) => {
                const selectedFIledId = isNumeric(allValues[key])
                const selectedItem = isNumeric(itemData[key])
                if (selectedFIledId && selectedItem && allValues[key] == itemData[key]) {
                  flag++
                }
              })
              return filedIdInAllValuesLength === flag
            })

            let counterValue = await counterApiCall()

            computedValue += counterValue
          }
        } else if (key === COMPUTED_RESERVED_KEYS.VERSION) {
          computedValue += version
        }
      }
    }),
  )
  await Promise.resolve(ComputedData)
  return computedValue
}

function computeTableColumnForRegularFields(
  params,
  formData,
  allValues,
  listDataMetadata,
  operation,
  selectedRows,
  reviewContext,
) {
  let computedValue
  let initValue = '' //assume CONCAT

  if (operation === COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN) {
    initValue = 0
  } else if (operation === COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN) {
    initValue = 1
  }

  computedValue = initValue

  //we assume that ther's only 1 param, it starts with ^^ and this param is the field id of the column
  const param = params[0]
  const fieldId = param.substring(2).split('.')[0]
  const key = param.substring(2).split('.')[1]
  let tableField
  formData?.forEach((field) => {
    let subFieldTemplateIds = field?.subfield_template_ids?.split(',')
    if (subFieldTemplateIds?.includes(fieldId)) {
      tableField = field
    }
  })
  if (tableField.is_selectable_table && reviewContext !== REVIEW_CONTEXTS.REVIEW) {
    let subFieldTemplateIds = tableField?.subfield_template_ids?.split(',')
    if (selectedRows['st_' + tableField.id]?.length > 0) {
      if (key === COMPUTED_RESERVED_KEYS.VALUE) {
        selectedRows['st_' + tableField.id]?.forEach((row) => {
          if (subFieldTemplateIds.includes(fieldId)) {
            if (operation === COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN) {
              computedValue *= allValues['f_' + fieldId + '__' + row]
            } else {
              computedValue += allValues['f_' + fieldId + '__' + row] //CONCAT_TABLE_COLUMN || SUM_TABLE_COLUMN
            }
          }
        })
      } else {
        const listDataIds = []
        selectedRows['st_' + tableField.id]?.forEach((row) => {
          if (subFieldTemplateIds.includes(fieldId)) {
            listDataIds.push(allValues['f_' + fieldId + '__' + row])
          }
        })

        for (const listDataId of listDataIds) {
          const ldmdMatch = listDataMetadata.find((lddm) => lddm.list_data_id == listDataId)

          const match =
            ldmdMatch?.list_data_metadata == null ? initValue : ldmdMatch?.list_data_metadata[key]

          if (operation === COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN) {
            computedValue *= match
          } else {
            //CONCAT_TABLE_COLUMN || SUM_TABLE_COLUMN
            computedValue += match
          }
        }
      }
    }
  } else {
    if (key === COMPUTED_RESERVED_KEYS.VALUE) {
      Object.keys(allValues).forEach((key) => {
        if (key.split('__')[0].split('_')[1] == fieldId) {
          if (operation === COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN) {
            computedValue *= allValues[key]
          } else {
            computedValue += allValues[key] //CONCAT_TABLE_COLUMN || SUM_TABLE_COLUMN
          }
        }
      })
    } else {
      const listDataIds = []
      Object.keys(allValues).forEach((avKey) => {
        if (avKey.split('__')[0].split('_')[1] == fieldId) {
          listDataIds.push(allValues[avKey])
        }
      })

      for (const listDataId of listDataIds) {
        const ldmdMatch = listDataMetadata.find((lddm) => lddm.list_data_id == listDataId)

        const match =
          ldmdMatch?.list_data_metadata == null ? initValue : ldmdMatch?.list_data_metadata[key]

        if (operation === COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN) {
          computedValue *= match
        } else {
          //CONCAT_TABLE_COLUMN || SUM_TABLE_COLUMN
          computedValue += match
        }
      }
    }
  }

  return computedValue
}

async function computeFieldAcrossProcess(
  params,
  pivotFieldTemplateId,
  pivotFieldListId,
  exclusions,
  operation,
  processTemplateId,
  axiosOptions,
) {
  // get all process data of processTemplateId,classified by process_reference_num and only with highest version of each activity
  let data = await memoizedGetProcessDataOnlyWithHighestVersionByProcessTemplateId(
    processTemplateId,
    axiosOptions,
  )

  //Here combining process data into a single object of each process
  let combinedProcessData = data.map((datum) => {
    return datum.reduce((acc, datum2) => {
      if (
        datum2.activity_type === ACTIVITY_TYPES.DATA_ENTRY &&
        datum2.status == PROCESS_ACTIVITY_STATUSES.CLOSED
      ) {
        acc = { ...acc, ...datum2.data }
      }
      return acc
    }, {})
  })

  let computedValue
  let initValue = '' //assume COMPUTED_OPERATION_TYPES.CONCAT_ACROSS_PROCESS
  if (operation === COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS) {
    initValue = 0
  }
  computedValue = initValue

  combinedProcessData.forEach((pd) => {
    if (pd['f_' + pivotFieldTemplateId] == pivotFieldListId) {
      let flag = true
      for (let value of exclusions) {
        if (value.list_data_id == pd['f_' + value.field_template_id]) {
          flag = false
          break
        }
      }
      if (flag) {
        params.forEach((param) => {
          if (param.startsWith('^^')) {
            let fieldId = param.substring(2).split('.')[0]
            computedValue += pd['f_' + fieldId]
          }
        })
      }
    }
  })

  return computedValue
}

async function computeForSubFields(
  params,
  _formData,
  allValues,
  listDataMetadata,
  operation,
  rowNum,
  subFieldId,
) {
  let computedValue
  let initValue = '' //assume COMPUTED_OPERATION_TYPES.CONCAT

  if (operation === COMPUTED_OPERATION_TYPES.SUM) {
    initValue = 0
  } else if (operation === COMPUTED_OPERATION_TYPES.MULTIPLY) {
    initValue = 1
  }

  computedValue = initValue
  for (let param of params) {
    if (!param.startsWith('^^') && !param.startsWith('**')) {
      computedValue += param
    } else if (param.startsWith('^^')) {
      let fieldId

      let plusOrMinusOne = 1
      if (
        operation === COMPUTED_OPERATION_TYPES.MULTIPLY ||
        operation === COMPUTED_OPERATION_TYPES.SUM
      ) {
        fieldId = param.substring(3).split('.')[0]

        plusOrMinusOne = param.substring(2).split('.')[0].charAt(0) === '-' ? -1 : 1
      } else {
        fieldId = param.substring(2).split('.')[0]
      }
      const key = param.substring(2).split('.')[1]
      if (key === COMPUTED_RESERVED_KEYS.VALUE) {
        //check if field match else check if sub field match
        if ('f_' + fieldId in allValues) {
          if (operation === COMPUTED_OPERATION_TYPES.MULTIPLY) {
            computedValue *= allValues['f_' + fieldId] * plusOrMinusOne
          } else if (operation === COMPUTED_OPERATION_TYPES.SUM) {
            computedValue += allValues['f_' + fieldId] * plusOrMinusOne
          } else {
            //assume CONCAT
            computedValue += allValues['f_' + fieldId]
          }
        } else if ('f_' + fieldId + '__' + rowNum in allValues) {
          if (operation === COMPUTED_OPERATION_TYPES.MULTIPLY) {
            computedValue *= allValues['f_' + fieldId + '__' + rowNum] * plusOrMinusOne
          } else if (operation === COMPUTED_OPERATION_TYPES.SUM) {
            computedValue += allValues['f_' + fieldId + '__' + rowNum] * plusOrMinusOne
          } else {
            //assume CONCAT
            computedValue += allValues['f_' + fieldId + '__' + rowNum]
          }
        } else {
          if (operation === COMPUTED_OPERATION_TYPES.MULTIPLY) {
            computedValue *= 0 * plusOrMinusOne
          }
        }
      } else {
        //key is from metadata

        //first assume field, else assume sub field
        let rawValue = allValues['f_' + fieldId]
        if (!rawValue) {
          rawValue = allValues['f_' + fieldId + '__' + rowNum]
        }

        // Check if referencing a multi-list field (comma-separated IDs stored)
        // We detect multilist by checking if rawValue contains a comma
        // or by the presence of multiple metadata matches
        const isMultiListValue =
          rawValue && String(rawValue).includes(',') && operation === COMPUTED_OPERATION_TYPES.CONCAT

        if (isMultiListValue) {
          const listDataIds = String(rawValue)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
          const metaValues = listDataIds
            .map((ldId) => {
              const ldmdMatch = listDataMetadata.find(
                (lddm) => lddm.id == fieldId && lddm.list_data_id == ldId,
              )
              return ldmdMatch?.list_data_metadata == null
                ? ''
                : ldmdMatch?.list_data_metadata[key] ?? ''
            })
            .filter(Boolean)
          computedValue += metaValues.join(', ')
        } else {
          const ldmdMatch = listDataMetadata.find(
            (lddm) => lddm.id == fieldId && lddm.list_data_id == rawValue,
          )
          const match =
            ldmdMatch?.list_data_metadata == null ? initValue : ldmdMatch?.list_data_metadata[key]

          if (operation === COMPUTED_OPERATION_TYPES.MULTIPLY) {
            computedValue *= match * plusOrMinusOne
          } else if (operation === COMPUTED_OPERATION_TYPES.SUM) {
            computedValue += match * plusOrMinusOne
          } else {
            //assume CONCAT
            computedValue += match
          }
        }
      }
    } else if (param.startsWith('**') && operation === COMPUTED_OPERATION_TYPES.CONCAT) {
      const key = param.substring(2)
      if (key == COMPUTED_RESERVED_KEYS.ROW_NUMBER) {
        let rowNumber = subFieldId.split('__')[1]
        computedValue += rowNumber
      }
    }
  }

  return computedValue
}

export function getProcessStatusByReferenceNum(refNum, allPD) {
  const filteredPD = allPD.filter(
    (pd) =>
      pd.process_reference_num === refNum &&
      pd.activity_type !== ACTIVITY_TYPES.PARALLEL_START &&
      pd.activity_type !== ACTIVITY_TYPES.PARALLEL_END,
  )

  const isEndAccepted = filteredPD.find((pd) => pd.activity_type === ACTIVITY_TYPES.END_ACCEPTED)
    ? true
    : false
  if (isEndAccepted) {
    return PROCESS_STATUSES.CLOSED_ACCEPTED
  }

  const isEndRejected = filteredPD.find((pd) => pd.activity_type === ACTIVITY_TYPES.END_REJECTED)
    ? true
    : false
  if (isEndRejected) {
    return PROCESS_STATUSES.CLOSED_REJECTED
  }

  //if there is no end-rejected or end-accepted activity, then the process with...
  //this particular ref num has to be active
  return PROCESS_STATUSES.ACTIVE
}

export function getProcessDashboardReportData(refNum, allPD, type, holidayList) {
  const filteredPD = allPD
    .filter(
      (pd) =>
        pd.process_reference_num == refNum &&
        pd.activity_type !== ACTIVITY_TYPES.PARALLEL_START &&
        pd.activity_type !== ACTIVITY_TYPES.PARALLEL_END,
    )
    .sort((a, b) => (a.id > b.id ? 1 : -1))

  let returnValue = ''
  switch (type) {
    case 'initiatedAt':
      returnValue = filteredPD[0]?.created_at
      break
    case 'closedAt':
      returnValue =
        filteredPD[filteredPD.length - 1]?.activity_type == ACTIVITY_TYPES.END_ACCEPTED ||
        filteredPD[filteredPD.length - 1]?.activity_type == ACTIVITY_TYPES.END_REJECTED
          ? filteredPD[filteredPD.length - 1]?.created_at
          : ''
      break

    case 'currentTask': {
      const activeNodes = filteredPD.filter((pd) => pd.status === PROCESS_ACTIVITY_STATUSES.ACTIVE)
      returnValue = activeNodes.length ? activeNodes.map((pd) => pd.activity_template_name) : []
      break
    }

    case 'currentAssignee': {
      const activeNodes = filteredPD.filter((pd) => pd.status === PROCESS_ACTIVITY_STATUSES.ACTIVE)
      returnValue = activeNodes.length ? activeNodes.map((pd) => pd.modified_by_user) : []
      break
    }

    case 'taskAssignedAt': {
      const activeNodes = filteredPD.filter((pd) => pd.status === PROCESS_ACTIVITY_STATUSES.ACTIVE)
      returnValue = activeNodes.length ? activeNodes.map((pd) => pd.created_at) : []
      break
    }

    case 'workingTime': {
      const now = new Date()

      const startDate = filteredPD[0]?.created_at ? new Date(filteredPD[0].created_at) : null

      const endDate =
        filteredPD[filteredPD.length - 1]?.activity_type === ACTIVITY_TYPES.END_ACCEPTED ||
        filteredPD[filteredPD.length - 1]?.activity_type === ACTIVITY_TYPES.END_REJECTED
          ? new Date(filteredPD[filteredPD.length - 1].modified_at)
          : now

      if (startDate && endDate) {
        const totalWorkMinutes = calculateEffectiveMinutes(startDate, endDate, holidayList)
        returnValue = Number(minutesToDays(totalWorkMinutes).toFixed(2))
      } else {
        returnValue = null
      }

      break
    }

    default:
      break
  }

  return returnValue
}

/* Here we will get 2D array of processData by processTemplateId,
    each inner array seperated by process_reference_num 
    and each processData contains only highest version of it's activity */
export const getProcessDataOnlyWithHighestVersionByProcessTemplateId = async (
  process_template_id,
  axiosOptions,
) => {
  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

  const data = (
    await axios.get(
      `${NODE_BASE_URL}/data-entry/process-data/process/${process_template_id}`,
      axiosOptions,
    )
  ).data

  let result = Object.values(
    data.reduce((acc, datum) => {
      if (acc['prn_' + datum.process_reference_num]) {
        acc['prn_' + datum.process_reference_num] = [
          ...acc['prn_' + datum.process_reference_num],
          datum,
        ]
      } else {
        acc['prn_' + datum.process_reference_num] = [datum]
      }
      return acc
    }, {}),
  )

  result = result.map((data) => {
    return Object.values(
      data.reduce((acc, datum) => {
        if (
          !acc['act_' + datum.activity_template_id] ||
          acc['act_' + datum.activity_template_id].version < datum.version
        ) {
          acc['act_' + datum.activity_template_id] = datum
        }
        return acc
      }, {}),
    )
  })

  return result
}

function memoizeWithExpiration(func, expirationTime) {
  const memoized = _.memoize(func)

  return async function (...args) {
    const result = await memoized(...args)

    // Add expiration logic
    setTimeout(() => {
      memoized.cache.clear()
    }, expirationTime)

    return result
  }
}

const memoizedGetProcessDataOnlyWithHighestVersionByProcessTemplateId = memoizeWithExpiration(
  getProcessDataOnlyWithHighestVersionByProcessTemplateId,
  3 * 60 * 1000,
)
